import {useTranslation} from 'react-i18next'
import type {CreativeAssetState, CreativeBackgroundMode} from '../../../common/utils/creativeAssetRenderer'
import CreativeImageSlot from './CreativeImageSlot'
import {ColorRow, Slider} from './layerControls'

interface BackgroundPanelProps {
  state: CreativeAssetState
  onChange: (patch: Partial<CreativeAssetState>) => void
}

const BACKGROUND_MODES: CreativeBackgroundMode[] = ['color', 'gradient', 'image']

/** Everything about the backdrop: fill type and colors / image. */
const BackgroundPanel = ({state, onChange}: BackgroundPanelProps) => {
  const {t} = useTranslation()

  return (
    <div className="caa-layers">
      <h2 className="caa-panel-title">{t('creativeAssets.panels.background')}</h2>
      <div className="caa-tabs caa-tabs-compact">
        {BACKGROUND_MODES.map((mode) => (
          <button
            key={mode}
            type="button"
            className={`caa-tab${state.bgMode === mode ? ' is-active' : ''}`}
            onClick={() => onChange({bgMode: mode})}
          >
            {t(`creativeAssets.background.${mode}`)}
          </button>
        ))}
      </div>
      {state.bgMode === 'color' && (
        <ColorRow
          label={t('creativeAssets.background.fill')}
          value={state.bgColor}
          onChange={(bgColor) => onChange({bgColor})}
        />
      )}
      {state.bgMode === 'gradient' && (
        <>
          <ColorRow
            label={t('creativeAssets.background.from')}
            value={state.bgFrom}
            onChange={(bgFrom) => onChange({bgFrom})}
          />
          <ColorRow
            label={t('creativeAssets.background.to')}
            value={state.bgTo}
            onChange={(bgTo) => onChange({bgTo})}
          />
          <Slider
            label={t('creativeAssets.background.angle')}
            value={state.bgAngle}
            min={0}
            max={360}
            step={1}
            format={(v) => `${v}°`}
            onChange={(bgAngle) => onChange({bgAngle})}
          />
        </>
      )}
      {state.bgMode === 'image' && (
        <CreativeImageSlot
          slot="background"
          label={t('creativeAssets.background.imageLabel')}
          image={state.bgImage}
          onImageLoaded={(bgImage) => onChange({bgImage})}
          onClear={() => onChange({bgImage: null})}
        />
      )}
    </div>
  )
}

export default BackgroundPanel
